import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, X } from "lucide-react";
import { ACTIVE_CHAIN } from "../contracts/chains";
import { useUsdcBalance, formatUSDC } from "../hooks/useGames";

interface Props {
  walletAddress: string | null;
  onClose: () => void;
}

/**
 * Polymarket-style deposit: your wallet IS your balance.
 *
 * No more "transfer USDT from wallet to vault" two-step. The modal just
 * surfaces the user's wallet address with copy + explorer affordances
 * and tells them where to send funds. Once USDC lands on this address
 * the live `Balance` ticker in WalletPanel updates and they can play.
 *
 * The previous flow (approve + vault.deposit) is gone — the games-side
 * contracts (HupperGames) pull directly from the wallet via Privy
 * session signers, so funds never need to leave it.
 */
export default function DepositModal({ walletAddress, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const usdc = useUsdcBalance(walletAddress, 15_000);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async () => {
    if (!walletAddress) return;
    await navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const explorerUrl =
    walletAddress && ACTIVE_CHAIN.explorerUrl
      ? `${ACTIVE_CHAIN.explorerUrl}/address/${walletAddress}`
      : null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-[#FF6B00] to-[#F54D12]" />

        <div className="p-5 flex items-center justify-between border-b border-white/5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-bold">
              {ACTIVE_CHAIN.name} · USDC
            </div>
            <div className="text-base font-semibold text-white">Deposit</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Current balance — tells the user "this is what's in your
              wallet right now" so they understand what `deposit` means. */}
          <div className="bg-gradient-to-br from-[#FF6B00]/10 to-[#0A0A0A] border border-[#FF6B00]/25 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-bold mb-1">
              Current balance
            </div>
            <div className="text-3xl font-bold tabular-nums text-white">
              {usdc.loading ? "…" : `$${formatUSDC(usdc.value)}`}
              <span className="text-sm text-white/40 font-semibold ml-1.5">USDC</span>
            </div>
            <div className="text-[11px] text-white/40 mt-1">
              Live in your wallet · ready to play
            </div>
          </div>

          <p className="text-[13px] text-white/65 leading-relaxed">
            Send <span className="text-white font-semibold">USDC on {ACTIVE_CHAIN.name}</span>{" "}
            to the address below. It lands in your wallet, and you can play
            immediately — Hupper never custodies your funds.
          </p>

          {/* Address — big, mono, copy + explorer */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-bold mb-2">
              Your wallet address
            </div>
            <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-3">
              <code className="flex-1 font-mono text-[13px] text-white break-all leading-snug">
                {walletAddress ?? "—"}
              </code>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={copy}
                disabled={!walletAddress}
                className="flex-1 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#F54D12] disabled:opacity-30 disabled:cursor-not-allowed text-black font-bold text-[13px] tracking-tight transition-colors inline-flex items-center justify-center gap-1.5"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy address
                  </>
                )}
              </button>
              {explorerUrl && (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2.5 rounded-lg border border-white/10 hover:border-white/30 hover:bg-white/5 text-white font-semibold text-[13px] tracking-tight transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Explorer
                </a>
              )}
            </div>
          </div>

          {/* Heads-up about the network — funds sent on the wrong chain
              are lost, so this banner is mandatory, not decorative. */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-[12px] text-amber-200/90 leading-relaxed">
            <span className="font-semibold text-amber-100">Only send on {ACTIVE_CHAIN.name}.</span>
            {" "}Funds sent from another chain — Ethereum, BSC, Arbitrum, etc — are lost
            and can't be recovered.
          </div>
        </div>
      </div>
    </div>
  );
}
